import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { Plus } from '@untitledui/icons/Plus';
import { useState } from 'react';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { ControlledModal } from '@/components/base/modals/modal';
import { notify } from '@/components/base/notifications/notifications';
import { TextArea } from '@/components/base/textarea/textarea';
import { queryKeys } from '@/hooks/api/query-keys';

import type { SsoProvider } from '../-utils/organization-settings-server';

import { ssoProvidersQueryOptions } from '../-utils/organization-settings-query';
import {
  createSsoProvider,
  deleteSsoProvider,
  updateSsoProvider,
} from '../-utils/organization-settings-server';
import { SettingsSection } from '../../settings/-components/settings-section';

export const Route = createFileRoute(
  '/researchers/_authenticated/organization-settings/organization/sso-providers',
)({
  component: SsoProvidersPage,
  head: () => ({ meta: [{ title: t`SSO providers` }] }),
});

const providerLabel = (p: SsoProvider) => p.name || p.id;

function SsoProvidersPage() {
  const { activeDashboard } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const providers = useQuery(
    ssoProvidersQueryOptions(activeDashboard.organization ?? ''),
  );
  const [editing, setEditing] = useState<SsoProvider | 'new' | null>(null);
  const [toggling, setToggling] = useState<SsoProvider | null>(null);
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.workspaceSettings.all(),
    });

  const toggle = useMutation({
    mutationFn: async (provider: SsoProvider) => {
      const { id, ...fields } = provider;
      const result = await updateSsoProvider({
        data: { ...fields, providerId: id, enabled: !provider.enabled },
      });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
      return !provider.enabled;
    },
    onSuccess: async (enabled) => {
      setToggling(null);
      notify({
        title: enabled ? t`Provider enabled` : t`Provider disabled`,
        color: 'success',
      });
      await refresh();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't update the provider`,
        description: error.message,
        color: 'error',
      }),
  });

  const enabledProvider = providers.data?.find((p) => p.enabled);

  return (
    <SettingsSection
      title={t`SSO providers`}
      description={t`Let people in your organization sign in through your identity provider. One provider can be enabled at a time.`}
    >
      {providers.isError ? (
        <AlertFloating
          color="error"
          title={t`We couldn't load SSO providers`}
          description={t`Refresh the page to try again.`}
          confirmLabel=""
        />
      ) : providers.isPending ? (
        <div className="h-32 animate-pulse rounded-lg bg-secondary" />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex justify-end">
            <Button
              color="secondary"
              size="sm"
              iconLeading={Plus}
              onClick={() => setEditing('new')}
            >
              <Trans>Add provider</Trans>
            </Button>
          </div>
          {providers.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-tertiary">
              <Trans>No SSO providers yet.</Trans>
            </p>
          ) : (
            <ul className="divide-y divide-secondary">
              {providers.data.map((provider) => (
                <li key={provider.id} className="flex items-center gap-3 py-3">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-primary">
                        {providerLabel(provider)}
                      </p>
                      {provider.enabled && (
                        <Badge type="pill-color" color="success" size="sm">
                          <Trans>Enabled</Trans>
                        </Badge>
                      )}
                    </div>
                    <p className="truncate font-mono text-xs text-tertiary">
                      {provider.id}
                    </p>
                  </div>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => setEditing(provider)}
                  >
                    <Trans>View or edit</Trans>
                  </Button>
                  <Button
                    color={
                      provider.enabled ? 'secondary-destructive' : 'secondary'
                    }
                    size="sm"
                    onClick={() => setToggling(provider)}
                  >
                    {provider.enabled ? (
                      <Trans>Disable</Trans>
                    ) : (
                      <Trans>Enable</Trans>
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {editing && (
        <ProviderEditor
          provider={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => void refresh()}
        />
      )}

      <ConfirmationModal
        isOpen={toggling !== null}
        onOpenChange={(open) => !open && setToggling(null)}
        title={
          toggling?.enabled
            ? t`Disable ${toggling ? providerLabel(toggling) : ''}?`
            : t`Enable ${toggling ? providerLabel(toggling) : ''}?`
        }
        description={
          toggling?.enabled
            ? t`People in your organization will no longer sign in through this provider.`
            : enabledProvider && enabledProvider.id !== toggling?.id
              ? t`It becomes the sign-in provider for everyone in your organization, replacing ${providerLabel(enabledProvider)}.`
              : t`It becomes the sign-in provider for everyone in your organization.`
        }
        confirmLabel={toggling?.enabled ? t`Disable` : t`Enable`}
        isDestructive={toggling?.enabled}
        isLoading={toggle.isPending}
        onConfirm={() => toggling && toggle.mutate(toggling)}
        onCancel={() => setToggling(null)}
      />
    </SettingsSection>
  );
}

function ProviderEditor({
  provider,
  onClose,
  onSaved,
}: {
  provider: SsoProvider | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { env } = useRouteContext({ from: '__root__' });
  const [name, setName] = useState(provider?.name ?? '');
  const [ssoUrl, setSsoUrl] = useState(provider?.ssoUrl ?? '');
  const [issuer, setIssuer] = useState(provider?.issuer ?? '');
  const [certificate, setCertificate] = useState(provider?.certificate ?? '');
  const [submitted, setSubmitted] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const urlValid = (() => {
    try {
      return Boolean(new URL(ssoUrl.trim()));
    } catch {
      return false;
    }
  })();
  const errors = {
    name: submitted && !name.trim() ? t`Name is required` : undefined,
    ssoUrl:
      submitted && !urlValid ? t`Enter the provider's sign-in URL` : undefined,
    certificate:
      submitted && !certificate.trim()
        ? t`Paste the public certificate`
        : undefined,
  };

  const save = useMutation({
    mutationFn: async () => {
      const fields = {
        name: name.trim(),
        ssoUrl: ssoUrl.trim(),
        issuer: issuer.trim(),
        certificate: certificate.trim(),
      };
      const result = provider
        ? await updateSsoProvider({
            data: {
              ...fields,
              providerId: provider.id,
              enabled: provider.enabled,
            },
          })
        : await createSsoProvider({ data: fields });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
    },
    onSuccess: () => {
      notify({
        title: provider ? t`Provider saved` : t`Provider added`,
        color: 'success',
      });
      onSaved();
      onClose();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't save the provider`,
        description: error.message,
        color: 'error',
      }),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!provider) return;
      const result = await deleteSsoProvider({
        data: { providerId: provider.id },
      });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
    },
    onSuccess: () => {
      notify({ title: t`Provider deleted`, color: 'success' });
      onSaved();
      onClose();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't delete the provider`,
        description: error.message,
        color: 'error',
      }),
  });

  // What the identity provider needs from Respondent; the website serves the
  // SAML endpoints.
  const callbackUrl = provider
    ? `${env.WEBSITE_URL}/auth/saml/${provider.id}/callback`
    : undefined;
  const entityId = provider
    ? `${env.WEBSITE_URL}/auth/saml/sp/${provider.id}`
    : undefined;

  return (
    <ControlledModal
      isOpen
      onOpenChange={(open) => !open && onClose()}
      centered
      title={provider ? t`Edit provider` : t`Add provider`}
      className="mx-auto w-full max-w-xl rounded-xl bg-primary p-6 shadow-xl"
    >
      <form
        className="flex w-full flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (name.trim() && urlValid && certificate.trim()) save.mutate();
        }}
      >
        <h2 className="text-lg font-semibold text-primary">
          {provider ? (
            <Trans>Edit provider</Trans>
          ) : (
            <Trans>Add provider</Trans>
          )}
        </h2>
        {callbackUrl && entityId && (
          <div className="flex flex-col gap-3 rounded-lg bg-secondary p-3">
            <Input
              label={t`Respondent SSO callback URL`}
              value={callbackUrl}
              isReadOnly
              size="xs"
            />
            <Input
              label={t`Respondent entity ID`}
              value={entityId}
              isReadOnly
              size="xs"
            />
          </div>
        )}
        <Input
          label={t`Name`}
          value={name}
          onChange={setName}
          isRequired
          isInvalid={!!errors.name}
          hint={errors.name}
          size="xs"
        />
        <Input
          label={t`SSO URL`}
          value={ssoUrl}
          onChange={setSsoUrl}
          placeholder="https://"
          isRequired
          isInvalid={!!errors.ssoUrl}
          hint={errors.ssoUrl}
          size="xs"
        />
        <Input
          label={t`Issuer ID / entity ID`}
          value={issuer}
          onChange={setIssuer}
          size="xs"
        />
        <TextArea
          label={t`Public certificate`}
          value={certificate}
          onChange={setCertificate}
          rows={5}
          isRequired
          isInvalid={!!errors.certificate}
          hint={errors.certificate}
          textAreaClassName="font-mono text-xs"
        />
        <div className="flex flex-wrap justify-between gap-3">
          {provider ? (
            <Button
              color="secondary-destructive"
              size="md"
              onClick={() => setIsDeleteOpen(true)}
            >
              <Trans>Delete provider</Trans>
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <Button color="secondary" size="md" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              color="primary"
              size="md"
              isLoading={save.isPending}
            >
              {provider ? <Trans>Save</Trans> : <Trans>Add provider</Trans>}
            </Button>
          </div>
        </div>
      </form>
      <ConfirmationModal
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title={t`Delete ${provider ? providerLabel(provider) : ''}?`}
        description={
          provider?.enabled
            ? t`It's the sign-in provider for everyone in your organization right now.`
            : t`It can't be used to sign in once it's deleted.`
        }
        confirmLabel={t`Delete provider`}
        isDestructive
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setIsDeleteOpen(false)}
      />
    </ControlledModal>
  );
}
